import React, { useCallback, useState } from 'react';
import { Close, Edit } from '@johnlewispartnership/wtr-style-dictionary/icons/react/Actions';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography/index.js';
import { Tooltip } from '@johnlewispartnership/wtr-ingredients/ingredients/Tooltip/index.js';
import classNames from 'classnames';
import { useSlotContext } from 'contexts/Slot';
import GoToCheckout from 'components/GoToCheckout';
import { formatSlotTime } from 'utils/format-slot-time';
import { dayjs, FORMAT } from 'utils/date';
import { getFulfilmentTypeForBookedSlot } from 'utils/get-fulfilment-type';
import { useTrolley } from 'contexts/Trolley';
import { getOrderType } from 'utils/get-order-type';
import { useShoppingContext } from 'contexts/ShoppingContext';
import {
  getDismissedAmendTooltipOrderId,
  isAmendOrderAlertTooltipOpen,
  setDismissedAmendTooltipOrderId,
} from 'utils/checkout/amend-order-tooltip';
import { useWtrSelector } from 'redux/hooks';
import { AmendCutOffMessage } from './AmendCutOffMessage';
import styles from './AmendOrderAlert.module.scss';

export interface AmendOrderAlertProps {
  isOpen?: boolean;
  onToggle?: () => void;
  onCancelChanges: () => void;
}

export const AmendOrderAlert = ({ isOpen, onCancelChanges, onToggle }: AmendOrderAlertProps) => {
  const {
    shoppingContext: { customerOrderId },
  } = useShoppingContext();
  const dismissedAmendTooltipOrderId = getDismissedAmendTooltipOrderId();
  const [isTooltipOpen, setIsTooltipOpen] = useState<boolean>(() =>
    isAmendOrderAlertTooltipOpen(dismissedAmendTooltipOrderId, customerOrderId),
  );
  const {
    slotContext: { details: slotDetails },
  } = useSlotContext();
  const { state: trolleyState } = useTrolley();
  const isBeforeYouGo = /^\/ecom\/beforeYouGo/i.test(window.location?.pathname);
  const isMobileOverlayOpen = useWtrSelector(state => state.page.isMobileOverlayOpen);

  const {
    amendOrderCutoffDateTime,
    slotType,
    startDateTime: slotStartTime,
    endDateTime: slotEndTime,
  } = slotDetails || {};

  const onAlertClose = useCallback(() => {
    setDismissedAmendTooltipOrderId(customerOrderId);
    setIsTooltipOpen(!isTooltipOpen);
  }, [customerOrderId, isTooltipOpen]);

  if (!slotDetails || !amendOrderCutoffDateTime || !slotType || !slotStartTime || !slotEndTime)
    return null;

  const orderType = getOrderType({
    slotType,
    containsEntertainingLines: trolleyState?.order?.containsEntertainingLines || false,
  });

  const {
    // @ts-ignore - confirmationPanel always exists on object but not detected due to an issue caused by spread operator
    confirmationPanel: { headerText: fulfilmentTypeMessage },
  } = getFulfilmentTypeForBookedSlot(slotType);

  const onExpandClick = () => {
    setIsTooltipOpen(false);
    onToggle?.();
  };

  return (
    <div className={styles.amendOrderAlert} data-testid="amendOrderAlert">
      <section className={classNames(styles.titleSection, { [styles.open]: isOpen })}>
        <div className={styles.iconContainer}>
          <Edit />
          <Typography className={styles.amendIconTxt} styleAs="paragraphHeading">
            AMENDING
          </Typography>
        </div>

        <button
          data-testid="expand-bar-btn"
          aria-label="Expand"
          className={styles.expandChevron}
          onClick={onExpandClick}
          type="button"
        />
      </section>
      <section className={styles.details}>
        <p className={styles.slotInfo}>
          <span className={styles.slotDate} data-testid="slotDate">
            {`${fulfilmentTypeMessage} on ${dayjs(slotStartTime).format(FORMAT.LONG_DAY_MONTH)}`}
          </span>
          <span className={styles.slotTime} data-testid="slotTime">
            {` ${formatSlotTime(slotStartTime)} - ${formatSlotTime(slotEndTime)}`}
          </span>
        </p>
        <p>
          <span className={styles.amendOrderCutOff} data-testid="amendOrderCutOff">
            <AmendCutOffMessage
              slotDate={slotStartTime}
              amendOrderCutOff={amendOrderCutoffDateTime}
              orderType={orderType}
            />
          </span>
        </p>
      </section>

      <section className={styles.ctas}>
        {/* // ProceedThroughCheckout component normally here but that uses unnecessary
        logic around interstitials for the PDP so simplified and the nested
        GoToCheckout component has been used instead */}
        <GoToCheckout
          messagePlacement="topPopover"
          buttonCheckoutId={
            isBeforeYouGo
              ? 'checkout-amend-order-interstitials-alert'
              : 'checkout-amend-order-alert'
          }
          theme="primaryWhite"
          location="Amend snackbar"
          useLightTooltip
        />
        <button
          className={styles.discard}
          data-testid="cancel-amendment-changes"
          onClick={onCancelChanges}
          type="button"
        >
          Discard amends
        </button>
      </section>
      <Tooltip
        content={
          <div className={styles.checkoutTooltipContent}>
            <Typography styleAs="paragraphSmall" className={styles.checkoutTooltipText}>
              Don&apos;t forget to checkout after
              <br />
              amending your order
            </Typography>
            <div>
              <Close size="xsmall" onClick={onAlertClose} style={{ cursor: 'pointer' }} />
            </div>
          </div>
        }
        placement="top-end"
        distanceOffset={25}
        skiddingOffset={0}
        disableFocusListener
        disableTouchListener
        disableHoverListener
        isOpen={isTooltipOpen && !isOpen && !isMobileOverlayOpen}
      >
        <span aria-hidden className={styles.tooltipReference} />
      </Tooltip>
    </div>
  );
};

AmendOrderAlert.displayName = 'AmendOrderAlert';
